import { useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Info } from 'lucide-react'
import { Tooltip, type TooltipRefProps } from 'react-tooltip'
import { cn } from '~/shadcn/lib/utils'
import { Button } from '~/shadcn/ui/button/button'

type Props = {
  text: string | React.ReactNode
  linkText?: string
  linkUrl?: string
  linkTarget?: string
  buttonText?: string
  buttonAction?: (() => void) | ((e: React.MouseEvent) => void)
  color?: string
  triggerContent?: string | React.ReactNode
  triggerClassname?: string
  iconClassName?: string
  clickable?: boolean
}

function TextTooltip({
  text,
  linkText,
  linkUrl,
  linkTarget = '_blank',
  buttonText,
  buttonAction,
  color,
  triggerContent,
  triggerClassname,
  iconClassName,
  clickable = true,
}: Props) {
  const generatedId = useId()
  const tooltipRef = useRef<TooltipRefProps>(null)

  const handleStopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation()
  }

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    tooltipRef.current?.close()
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (buttonAction) {
      buttonAction(e)
    }
    tooltipRef.current?.close()
  }

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <>
      <div
        data-tooltip-id={generatedId}
        onClick={handleStopPropagation}
        onPointerDown={handleStopPropagation}
        className={cn(
          'gap-2 p-0 h-auto inline-flex justify-center self-center focus-visible:ring-0 focus-visible:ring-offset-0 cursor-pointer align-baseline z-10',
          triggerClassname,
          triggerContent != null
            ? 'flex flex-row items-center cursor-pointer text-accent'
            : ''
        )}>
        <Info
          className={cn(
            'w-4 h-4 text-border',
            triggerContent != null ? 'text-accent' : '',
            iconClassName
          )}
          style={{
            color,
          }}
        />
        {triggerContent}
      </div>

      {createPortal(
        <Tooltip
          id={generatedId}
          ref={tooltipRef}
          noArrow={true}
          clickable={clickable}
          opacity={1}
          className="w-fit max-w-[min(400px,calc(100vw-4rem))] px-4 py-3 normal-case z-100! opacity-100! border border-primary text-primary! text-start rounded-md!"
          style={{
            backgroundColor: 'var(--color-accent-foreground)',
            color: 'var(--color-text-primary)',
            zIndex: 999999,
          }}
          place="top-end">
          <div
            onClick={handleContentClick}
            className="flex flex-col cursor-pointer">
            {typeof text === 'string' ? (
              <p className="text-[13px] font-semibold text-primary leading-normal text-wrap">
                {text}
              </p>
            ) : (
              text
            )}

            {linkText && linkUrl && (
              <Link
                to={linkUrl}
                target={linkTarget}
                rel={
                  linkTarget === '_blank' ? 'noopener noreferrer' : undefined
                }
                onClick={handleLinkClick}
                className="transition-all duration-300 ease-in-out inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-base disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer font-bold bg-accent text-white hover:bg-[color-mix(in_srgb,var(--color-accent),black_15%)] h-8 px-5 mt-2 mb-1">
                {linkText}
              </Link>
            )}

            {buttonText && buttonAction && (
              <Button
                variant="link"
                className="w-fit h-5 p-0 text-[13px] underline underline-offset-1"
                onClick={handleButtonClick}>
                {buttonText}
              </Button>
            )}
          </div>
        </Tooltip>,
        document.body
      )}
    </>
  )
}

export { TextTooltip }
